<template>
  <Popover
    class="select"
    :modifiers="[popoverModifier]"
    :class="{
      clearable,
      empty: !hasValue,
    }"
    placement="bottom-start"
    :open="open"
    @close="handleClose"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <div
      class="reference p-2 flex items-center"
      :class="{'w-full': fullWidth}"
      @click="handleOpen"
      slot="reference"
      v-bind="referenceProps"
    >
      <slot name="reference" v-bind="value">
        <SelectReference
          v-if="multiple ? selectedNodes.length > 0 : selectedNodes"
          @unselect="handleSelect"
          :multiple="multiple"
          :nodes="selectedNodes"
        />
        <template v-else>
          {{ noSelectionMessage }}
        </template>
      </slot>

      <div class="ml-auto">
        <Icon
          class="expanded-icon"
          :name="open ? 'chevron-up' : 'chevron-down'"
        />
        <Icon
          v-if="clearable"
          @click.stop="handleClear"
          class="clear-icon cursor-pointer"
          name="times-circle"
        />
      </div>
    </div>

    <ul class="list-none w-full">
      <slot />
    </ul>
  </Popover>
</template>

<script>
import Icon from '../Icon';
import Popover from '../Popover';
import cloneDeep from 'lodash/cloneDeep';
import wrapArray from '@converdy/utils/wrap-array';
import arrayToggle from '@converdy/utils/array-toggle';
import SelectReference from './SelectReference';

export default {
  name: 'Select',
  components: {
    SelectReference,
    Popover,
    Icon,
    Vnodes: {
      functional: true,
      render: (h, ctx) => h('h1', ctx.props.vnodes),
    },
  },
  props: {
    value: {
      default: undefined,
    },
    multiple: {
      default: false,
    },
    allowsEmpty: {
      default: true,
    },
    noSelectionMessage: {
      type: String,
      default: 'Select',
    },
    referenceProps: {
      default: Object,
    },
    closeOnSelect: {
      default: true,
    },
    clearable: {
      default: true,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
  },

  reactiveProvide: {
    name: 'Select',
    include: ['value', 'handleSelect'],
  },

  data() {
    return {
      open: false,
      popoverModifier: {
        name: 'sameWidth',
        enabled: true,
        phase: 'beforeWrite',
        requires: ['computeStyles'],
        fn: ({state}) => {
          state.styles.popper.minWidth = `${state.rects.reference.width}px`;
        },
        effect: ({state}) => {
          state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
        },
      },
      forcedSlotUpdate: false,
    };
  },

  methods: {
    handleSelect(value) {
      const emitValue = this.multiple
        ? arrayToggle(cloneDeep(this._value), value)
        : value;

      // If multiple select and allowsEmpty is false, prevent emit
      if (this.multiple && !this.allowsEmpty && emitValue.length === 0) {
        this.dispatchToast({
          type: 'error',
          content: '',
          title: 'Atleast one item should be selected',
          position: 'top-right',
          dismissible: true,
          dismissIn: 2000,
          icon: 'exclamation-circle text-danger',
        });
      } else {
        this.$emit('input', emitValue);
        this.$emit('change', emitValue);
      }

      if (this.closeOnSelect) {
        this.open = false;
      }
    },

    handleClose() {
      this.open = false;
      this.$emit('close');
    },

    handleOpen() {
      this.open = true;
      this.$emit('open');
    },

    handleClear() {
      this.$emit('input', undefined);
      this.$emit('change', undefined);
      this.handleClose();
    },
  },
  beforeUpdate() {
    // Update forcedSlotUpdate to force a re-render
    if (this.$slots.default) {
      this.forcedSlotUpdate = Math.random();
    }
  },
  computed: {
    _value() {
      return this.multiple ? wrapArray(this.value) : this.value;
    },
    hasValue() {
      return typeof this.value !== undefined && typeof this.value !== null;
    },
    selectedNodes() {
      let slotsInComputedPropAreNotReactiveFix = this.forcedSlotUpdate;

      // Check if there is a value
      if (this.hasValue && this.$slots.default) {
        // Filter selected nodes
        const nodes = this.$slots.default.filter(
          (vNode) =>
            vNode.componentOptions &&
            vNode.componentOptions.tag === 'Option' &&
            valueIncludes(vNode.componentOptions.propsData.value, this._value),
        );

        // Retun node or nodes
        return this.multiple ? nodes : nodes[0];
      }
    },
  },
};

function valueIncludes(value, internalValue) {
  if (typeof internalValue === 'string' || typeof internalValue === 'number') {
    return internalValue === value;
  } else if (Array.isArray(internalValue)) {
    return internalValue.includes(value);
  }
}
</script>

<style lang="scss">
.select {
  .clear-icon {
    display: none;
    @apply text-sm;
  }

  .expanded-icon {
    display: block;
    @apply text-sm;
  }

  &.clearable:not(.empty):hover {
    .clear-icon {
      display: block;
    }

    .expanded-icon {
      display: none;
    }
  }

  .reference {
    min-height: 40px;
    // max-width: 500px;
    @apply cursor-pointer text-secondary bg-white border border-dull rounded;
  }

  .list-none {
    max-height: 240px;
    overflow: auto;
  }
}
</style>
